import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { GooglePlacesAutocomplete } from '../../components/ui/google-places-autocomplete';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Send,
  Building2,
  Loader2,
  Wrench,
  HardHat,
  Package,
  Boxes,
  FileCheck,
  Camera,
  X,
  ChevronDown,
  ChevronUp,
  Calculator,
  MapPin,
  User
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const ITEM_TYPES = [
  { value: 'equipment', label: 'Asset', icon: Wrench },
  { value: 'labour', label: 'Labour', icon: HardHat },
  { value: 'attachment', label: 'Attachment', icon: Package },
  { value: 'material', label: 'Material', icon: Boxes },
  { value: 'custom', label: 'Custom', icon: FileCheck }
];

const UNITS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'each', label: 'Each' },
  { value: 'tonne', label: 'Tonne' },
  { value: 'm3', label: 'm³' },
  { value: 'km', label: 'km' },
  { value: 'load', label: 'Load' }
];

const MobileQuoteEdit = () => {
  const { api } = useMobileAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id && id !== 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState('');

  // Quick-add sheets (mirrors mobile job create)
  const [showCustomerSheet, setShowCustomerSheet] = useState(false);
  const [customerForm, setCustomerForm] = useState({ company_name: '', phone: '', email: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [showSiteSheet, setShowSiteSheet] = useState(false);
  const [siteForm, setSiteForm] = useState({ name: '', street_address: '', suburb: '', state: '', postcode: '' });
  const [savingSite, setSavingSite] = useState(false);
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', job_title: '', phone: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);
  
  // Reference data for dropdowns
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [finance, setFinance] = useState({ currency_symbol: '$', tax_name: 'GST', tax_rate: 10 });

  // Section expansion states
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    description: false,
    lineItems: true,
    photos: false,
    terms: false
  });

  const [formData, setFormData] = useState({
    customer_id: '',
    site_id: '',
    contact_id: '',
    pricing_type: 'hourly',
    valid_until: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    description: '',
    line_items: [],
    terms_and_conditions: '',
    notes: '',
    photos: []
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax_rate: 10,
    tax_amount: 0,
    total: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          customersRes, 
          equipmentTypesRes, 
          workerTypesRes, 
          attachmentTypesRes,
          materialsRes,
          termsRes,
          taxRes
        ] = await Promise.all([
          api.get('/api/customers', { params: { status: 'active' } }),
          api.get('/api/equipment-types'),
          api.get('/api/worker-types'),
          api.get('/api/attachment-types').catch(() => ({ data: [] })),
          api.get('/api/tips').catch(() => ({ data: [] })),
          api.get('/api/quotes/settings/terms'),
          api.get('/api/tenant/tax-settings').catch(() => ({ data: {} }))
        ]);
        
        setCustomers(customersRes.data.items || customersRes.data || []);
        setEquipmentTypes(equipmentTypesRes.data || []);
        setWorkerTypes(workerTypesRes.data || []);
        setAttachmentTypes(attachmentTypesRes.data || []);
        setMaterials(materialsRes.data || []);
        setDefaultTerms(termsRes.data.default_quote_terms || '');
        const fin = { currency_symbol: '$', tax_name: 'GST', tax_rate: 10, ...(taxRes.data || {}) };
        setFinance(fin);

        if (isEditing) {
          const quoteRes = await api.get(`/api/quotes/${id}`);
          const quote = quoteRes.data;
          setFormData({
            customer_id: quote.customer_id,
            site_id: quote.site_id || '',
            contact_id: quote.contact_id || '',
            pricing_type: quote.pricing_type,
            valid_until: quote.valid_until,
            description: quote.description || '',
            line_items: quote.line_items || [],
            terms_and_conditions: quote.terms_and_conditions || '',
            notes: quote.notes || '',
            photos: quote.photos || []
          });
          setTotals({
            subtotal: quote.subtotal,
            tax_rate: quote.tax_rate,
            tax_amount: quote.tax_amount,
            total: quote.total
          });
          
          if (quote.customer_id) {
            const sitesRes = await api.get(`/api/sites?customer_id=${quote.customer_id}`);
            setSites(sitesRes.data);
            try {
              const contactsRes = await api.get(`/api/customers/${quote.customer_id}/contacts`);
              setCustomerContacts(contactsRes.data || []);
            } catch { setCustomerContacts([]); }
          }
        } else {
          setFormData(prev => ({
            ...prev,
            terms_and_conditions: termsRes.data.default_quote_terms || ''
          }));
          setTotals(prev => ({ ...prev, tax_rate: fin.tax_rate ?? 10 }));
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, id, isEditing]);

  useEffect(() => {
    const subtotal = formData.line_items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.rate || 0));
    }, 0);
    const tax_amount = Math.round(subtotal * (totals.tax_rate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax_amount) * 100) / 100;

    setTotals(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      tax_amount,
      total
    }));
  }, [formData.line_items, totals.tax_rate]);

  const fetchCustomerContacts = async (customerId) => {
    if (!customerId) { setCustomerContacts([]); return; }
    try {
      const res = await api.get(`/api/customers/${customerId}/contacts`);
      setCustomerContacts(res.data || []);
    } catch { setCustomerContacts([]); }
  };

  const handleCustomerChange = async (customerId) => {
    if (customerId === '__add_new__') {
      setCustomerForm({ company_name: '', phone: '', email: '' });
      setShowCustomerSheet(true);
      return;
    }
    setFormData(prev => ({ ...prev, customer_id: customerId, site_id: '', contact_id: '' }));
    setCustomerContacts([]);

    if (customerId) {
      try {
        const sitesRes = await api.get(`/api/sites?customer_id=${customerId}`);
        setSites(sitesRes.data);
      } catch (error) {
        setSites([]);
      }
      fetchCustomerContacts(customerId);
    } else {
      setSites([]);
    }
  };

  const handleSiteSelect = (value) => {
    if (value === '__add_new__') {
      if (!formData.customer_id) { toast.error('Please select a customer first'); return; }
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '' });
      setShowSiteSheet(true);
      return;
    }
    setFormData(prev => ({ ...prev, site_id: value }));
  };

  const handleContactSelect = (value) => {
    if (value === '__add_new__') {
      if (!formData.customer_id) { toast.error('Please select a customer first'); return; }
      setContactForm({ name: '', job_title: '', phone: '', email: '' });
      setShowContactSheet(true);
      return;
    }
    setFormData(prev => ({ ...prev, contact_id: value === '__none__' ? '' : value }));
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.company_name.trim()) { toast.error('Company name is required'); return; }
    setSavingCustomer(true);
    try {
      const res = await api.post('/api/customers', {
        customer_type: 'organisation',
        company_name: customerForm.company_name.trim(),
        phone: customerForm.phone.trim() || null,
        email: customerForm.email.trim() || null,
      });
      toast.success('Customer created');
      setShowCustomerSheet(false);
      const refreshed = await api.get('/api/customers', { params: { status: 'active' } });
      setCustomers(refreshed.data.items || refreshed.data || []);
      setFormData(prev => ({ ...prev, customer_id: res.data.id, site_id: '', contact_id: '' }));
      setSites([]);
      setCustomerContacts([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleCreateSite = async () => {
    if (!formData.customer_id) return;
    if (!siteForm.street_address.trim()) { toast.error('Address is required'); return; }
    setSavingSite(true);
    try {
      const res = await api.post('/api/sites', { ...siteForm, customer_id: formData.customer_id });
      toast.success('Site created');
      setShowSiteSheet(false);
      const refreshed = await api.get(`/api/sites?customer_id=${formData.customer_id}`);
      setSites(refreshed.data);
      setFormData(prev => ({ ...prev, site_id: res.data.id }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create site');
    } finally {
      setSavingSite(false);
    }
  };

  const handleCreateContact = async () => {
    if (!formData.customer_id) return;
    if (!contactForm.name.trim()) { toast.error('Name is required'); return; }
    setSavingContact(true);
    try {
      const res = await api.post(`/api/customers/${formData.customer_id}/contacts`, { ...contactForm, customer_id: formData.customer_id });
      toast.success('Contact created');
      setShowContactSheet(false);
      await fetchCustomerContacts(formData.customer_id);
      setFormData(prev => ({ ...prev, contact_id: res.data.id }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create contact');
    } finally {
      setSavingContact(false);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          id: uuidv4(),
          item_type: 'equipment',
          description: '',
          equipment_id: '',
          equipment_type: '',
          worker_type_id: '',
          attachment_type_id: '',
          material_id: '',
          quantity: 1,
          unit: 'hour',
          rate: 0,
          total: 0,
          notes: ''
        }
      ]
    }));
    setExpandedSections(prev => ({ ...prev, lineItems: true }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'rate') {
        newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
      }
      
      if (field === 'equipment_type' && value) {
        newItems[index].description = value;
      }
      if (field === 'worker_type_id' && value) {
        const selected = workerTypes.find(w => w.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      if (field === 'attachment_type_id' && value) {
        const selected = attachmentTypes.find(a => a.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      if (field === 'material_id' && value) {
        const selected = materials.find(m => m.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      
      if (field === 'item_type') {
        newItems[index].equipment_id = '';
        newItems[index].equipment_type = '';
        newItems[index].worker_type_id = '';
        newItems[index].attachment_type_id = '';
        newItems[index].material_id = '';
        newItems[index].description = '';
      }
      
      return { ...prev, line_items: newItems };
    });
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await api.post('/api/quotes/upload-photo', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.url) {
          newPhotos.push(response.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newPhotos.length > 0) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
      }));
      toast.success(`Uploaded ${newPhotos.length} photo(s)`);
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (sendAfterSave = false) => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    setSaving(true);
    try {
      let savedQuote;
      
      if (isEditing) {
        const response = await api.put(`/api/quotes/${id}`, formData);
        savedQuote = response.data;
        toast.success('Quote updated');
      } else {
        const response = await api.post('/api/quotes', formData);
        savedQuote = response.data;
        toast.success('Quote created');
      }

      if (sendAfterSave && savedQuote) {
        await api.post(`/api/quotes/${savedQuote.id}/send`);
        toast.success('Quote sent to customer');
      }

      navigate('/mobile/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || '';
  };

  const renderLineItemTypeSelector = (item, index) => {
    const itemType = item.item_type || 'equipment';
    
    switch (itemType) {
      case 'equipment':
        return (
          <Select
            value={item.equipment_type || ''}
            onValueChange={(v) => updateLineItem(index, 'equipment_type', v)}
          >
            <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)]">
              <SelectValue placeholder="Select equipment type" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
              {equipmentTypes.map((type) => (
                <SelectItem key={type} value={type} className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'labour':
        return (
          <Select
            value={item.worker_type_id || ''}
            onValueChange={(v) => updateLineItem(index, 'worker_type_id', v)}
          >
            <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)]">
              <SelectValue placeholder="Select worker type" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
              {workerTypes.map((type) => (
                <SelectItem key={type.id} value={type.id} className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'attachment':
        return (
          <Select
            value={item.attachment_type_id || ''}
            onValueChange={(v) => updateLineItem(index, 'attachment_type_id', v)}
          >
            <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)]">
              <SelectValue placeholder="Select attachment" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
              {attachmentTypes.map((type) => (
                <SelectItem key={type.id} value={type.id} className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'material':
        return (
          <Select
            value={item.material_id || ''}
            onValueChange={(v) => updateLineItem(index, 'material_id', v)}
          >
            <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)]">
              <SelectValue placeholder="Select material" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
              {materials.map((m) => (
                <SelectItem key={m.id} value={m.id} className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-20 safe-top">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/mobile/quotes')}
            className="text-[var(--m-text)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-[var(--m-text)] font-bold text-lg flex-1">
            {isEditing ? 'Edit Quote' : 'New Quote'}
          </h1>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-6">
        {/* Customer Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('customer')}
          >
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">Customer Details</span>
              {formData.customer_id && (
                <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                  {getCustomerName(formData.customer_id)}
                </Badge>
              )}
            </div>
            {expandedSections.customer ? (
              <ChevronUp className="h-5 w-5 text-[var(--m-text-secondary)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--m-text-secondary)]" />
            )}
          </button>
          
          {expandedSections.customer && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              <div>
                <Label className="text-[var(--m-text-secondary)] text-sm">Customer <span className="text-red-500">*</span></Label>
                <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="mobile-quote-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] max-h-60">
                    <SelectItem value="__add_new__" className="text-[#ffd108] font-medium hover:bg-[var(--m-bg-elevated)]" data-testid="mobile-quote-add-customer-option">
                      <span className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add New Customer</span>
                    </SelectItem>
                    {customers.map((customer) => (
                      <SelectItem 
                        key={customer.id} 
                        value={customer.id}
                        className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                      >
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.customer_id && (
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-sm">Site (Optional)</Label>
                  <Select 
                    value={formData.site_id} 
                    onValueChange={handleSiteSelect}
                  >
                    <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="mobile-quote-site-select">
                      <SelectValue placeholder="Select site" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] max-h-60">
                      <SelectItem value="__add_new__" className="text-[#ffd108] font-medium hover:bg-[var(--m-bg-elevated)]" data-testid="mobile-quote-add-site-option">
                        <span className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add New Site</span>
                      </SelectItem>
                      {sites.map((site) => (
                        <SelectItem 
                          key={site.id} 
                          value={site.id}
                          className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                        >
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.customer_id && (
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-sm">Contact (Optional)</Label>
                  <Select 
                    value={formData.contact_id || '__none__'} 
                    onValueChange={handleContactSelect}
                  >
                    <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="mobile-quote-contact-select">
                      <SelectValue placeholder="Select contact" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] max-h-60">
                      <SelectItem value="__add_new__" className="text-[#ffd108] font-medium hover:bg-[var(--m-bg-elevated)]" data-testid="mobile-quote-add-contact-option">
                        <span className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add New Contact</span>
                      </SelectItem>
                      <SelectItem value="__none__" className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">No contact (send to customer email)</SelectItem>
                      {customerContacts.map((contact) => (
                        <SelectItem 
                          key={contact.id} 
                          value={contact.id}
                          className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                        >
                          {contact.name}{contact.job_title ? ` — ${contact.job_title}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[var(--m-text-muted)] mt-1">The selected contact receives the quote when sent.</p>
                </div>
              )}

              {formData.customer_id && (
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-sm">Email (Quote sent to)</Label>
                  <Input
                    data-testid="mobile-quote-recipient-email"
                    readOnly
                    value={
                      (formData.contact_id && customerContacts.find(c => c.id === formData.contact_id)?.email) ||
                      customers.find(c => c.id === formData.customer_id)?.email ||
                      ''
                    }
                    placeholder="No email on file"
                    className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1 cursor-default"
                  />
                  <p className="text-xs text-[var(--m-text-muted)] mt-1">
                    {formData.contact_id ? "From the selected contact." : "From the customer record — select a contact to override."}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-sm">Pricing Type</Label>
                  <Select 
                    value={formData.pricing_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, pricing_type: v }))}
                  >
                    <SelectTrigger className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
                      <SelectItem value="hourly" className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">Hourly</SelectItem>
                      <SelectItem value="daily" className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">Daily</SelectItem>
                      <SelectItem value="fixed" className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]">Fixed Price</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-sm">Valid Until</Label>
                  <DatePicker
                    value={formData.valid_until}
                    onChange={(d) => setFormData(prev => ({ ...prev, valid_until: d }))}
                    className="w-full bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Description Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('description')}
          >
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">Work Description</span>
            </div>
            {expandedSections.description ? (
              <ChevronUp className="h-5 w-5 text-[var(--m-text-secondary)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--m-text-secondary)]" />
            )}
          </button>
          
          {expandedSections.description && (
            <CardContent className="pt-0 pb-4 px-4">
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the work to be performed..."
                className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] min-h-[100px]"
              />
            </CardContent>
          )}
        </Card>

        {/* Line Items Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('lineItems')}
          >
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">Line Items</span>
              <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                {formData.line_items.length}
              </Badge>
            </div>
            {expandedSections.lineItems ? (
              <ChevronUp className="h-5 w-5 text-[var(--m-text-secondary)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--m-text-secondary)]" />
            )}
          </button>
          
          {expandedSections.lineItems && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              {formData.line_items.map((item, index) => {
                const TypeIcon = ITEM_TYPES.find(t => t.value === item.item_type)?.icon || FileCheck;
                return (
                  <div key={item.id} className="bg-[var(--m-bg-elevated)] rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-[#ffd108]" />
                        <span className="text-[var(--m-text-secondary)] text-sm">Item {index + 1}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs">Type</Label>
                      <Select 
                        value={item.item_type} 
                        onValueChange={(v) => updateLineItem(index, 'item_type', v)}
                      >
                        <SelectTrigger className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
                          {ITEM_TYPES.map((type) => {
                            const Icon = type.icon;
                            return (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  {type.label}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {item.item_type !== 'custom' && (
                      <div>
                        <Label className="text-[var(--m-text-secondary)] text-xs">
                          {item.item_type === 'equipment' ? 'Equipment Type' :
                           item.item_type === 'labour' ? 'Worker Type' :
                           item.item_type === 'attachment' ? 'Attachment' : 'Material'}
                        </Label>
                        <div className="mt-1">
                          {renderLineItemTypeSelector(item, index)}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[var(--m-text-secondary)] text-xs">Qty</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--m-text-secondary)] text-xs">Unit</Label>
                        <Select 
                          value={item.unit} 
                          onValueChange={(v) => updateLineItem(index, 'unit', v)}
                        >
                          <SelectTrigger className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border-light)]">
                            {UNITS.map((unit) => (
                              <SelectItem 
                                key={unit.value} 
                                value={unit.value}
                                className="text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
                              >
                                {unit.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[var(--m-text-secondary)] text-xs">Rate ({finance.currency_symbol})</Label>
                        <Input
                          type="number"
                          value={item.rate}
                          onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <span className="text-[#ffd108] font-medium">
                        {finance.currency_symbol}{((item.quantity || 0) * (item.rate || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })}

              <Button
                variant="outline"
                onClick={addLineItem}
                className="w-full bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#4a4a4a] hover:border-[#ffd108]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Line Item
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Photos Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('photos')}
          >
            <div className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">Reference Photos</span>
              {formData.photos.length > 0 && (
                <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                  {formData.photos.length}
                </Badge>
              )}
            </div>
            {expandedSections.photos ? (
              <ChevronUp className="h-5 w-5 text-[var(--m-text-secondary)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--m-text-secondary)]" />
            )}
          </button>
          
          {expandedSections.photos && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {formData.photos.map((photo, index) => (
                    <div key={`photo-${index}`} className="relative group aspect-square">
                      <img
                        src={photo}
                        alt={`Reference ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 rounded-full p-1"
                      >
                        <X className="h-3 w-3 text-[var(--m-text)]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#4a4a4a]"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : 'Add Photos'}
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Terms Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('terms')}
          >
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">Terms & Notes</span>
            </div>
            {expandedSections.terms ? (
              <ChevronUp className="h-5 w-5 text-[var(--m-text-secondary)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--m-text-secondary)]" />
            )}
          </button>
          
          {expandedSections.terms && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              <div>
                <Label className="text-[var(--m-text-secondary)] text-sm">Terms & Conditions</Label>
                <Textarea
                  value={formData.terms_and_conditions}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                  placeholder="Enter terms and conditions..."
                  className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] min-h-[100px] mt-1"
                />
              </div>
              <div>
                <Label className="text-[var(--m-text-secondary)] text-sm">Internal Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes (not visible to customer)..."
                  className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] min-h-[80px] mt-1"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Totals Summary */}
        <Card className="bg-[var(--m-bg-card)] border-[#ffd108]/30">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-[var(--m-text-secondary)]">
                <span>Subtotal</span>
                <span>${totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-[var(--m-text-secondary)]">
                <span>GST ({totals.tax_rate}%)</span>
                <span>${totals.tax_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-[var(--m-border-light)] pt-2 flex justify-between text-[var(--m-text)] font-bold text-lg">
                <span>Total</span>
                <span className="text-[#ffd108]">${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Actions — inline at end of form */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex-1 h-12 bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#4a4a4a]"
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex-1 h-12 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            data-testid="save-and-send-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Save & Send
          </Button>
        </div>
      </div>
    </div>

    {/* QUICK ADD CUSTOMER */}
    <Sheet open={showCustomerSheet} onOpenChange={setShowCustomerSheet}>
      <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[var(--m-text)] flex items-center gap-2"><Building2 className="h-5 w-5 text-[#ffd108]" /> Add New Customer</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Company Name <span className="text-red-500">*</span></Label>
            <Input data-testid="mobile-quote-customer-name-input" value={customerForm.company_name} onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })} placeholder="Acme Pty Ltd" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Phone</Label>
            <Input data-testid="mobile-quote-customer-phone-input" value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} placeholder="0400 000 000" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Email</Label>
            <Input data-testid="mobile-quote-customer-email-input" type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} placeholder="accounts@acme.com" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
        </div>
        <SheetFooter>
          <Button onClick={handleCreateCustomer} disabled={savingCustomer} className="w-full h-12 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90" data-testid="mobile-quote-customer-submit-btn">
            {savingCustomer ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add Customer
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {/* QUICK ADD SITE */}
    <Sheet open={showSiteSheet} onOpenChange={setShowSiteSheet}>
      <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[var(--m-text)] flex items-center gap-2"><MapPin className="h-5 w-5 text-[#ffd108]" /> Add New Site</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Search Address</Label>
            <GooglePlacesAutocomplete
              placeholder="Start typing an address..."
              onPlaceSelect={(place) => {
                setSiteForm(prev => ({
                  ...prev,
                  name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                  street_address: place.street_address || '',
                  suburb: place.suburb || '',
                  state: place.state || '',
                  postcode: place.postcode || ''
                }));
              }}
              data-testid="mobile-quote-site-address-autocomplete"
            />
          </div>
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Site Name</Label>
            <Input data-testid="mobile-quote-site-name-input" value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="e.g. Main Depot" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Street Address <span className="text-red-500">*</span></Label>
            <Input data-testid="mobile-quote-site-street-input" value={siteForm.street_address} onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })} placeholder="123 Main Street" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-sm">Suburb</Label>
              <Input data-testid="mobile-quote-site-suburb-input" value={siteForm.suburb} onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })} placeholder="Suburb" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
            </div>
            <div>
              <Label className="text-[var(--m-text-secondary)] text-sm">State</Label>
              <Input data-testid="mobile-quote-site-state-input" value={siteForm.state} onChange={(e) => setSiteForm({ ...siteForm, state: e.target.value })} placeholder="State" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
            </div>
            <div>
              <Label className="text-[var(--m-text-secondary)] text-sm">Postcode</Label>
              <Input data-testid="mobile-quote-site-postcode-input" value={siteForm.postcode} onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })} placeholder="0000" maxLength={4} className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
            </div>
          </div>
        </div>
        <SheetFooter>
          <Button onClick={handleCreateSite} disabled={savingSite} className="w-full h-12 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90" data-testid="mobile-quote-site-submit-btn">
            {savingSite ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add Site
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {/* QUICK ADD CONTACT */}
    <Sheet open={showContactSheet} onOpenChange={setShowContactSheet}>
      <SheetContent side="bottom" className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[var(--m-text)] flex items-center gap-2"><User className="h-5 w-5 text-[#ffd108]" /> Add New Contact</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Name <span className="text-red-500">*</span></Label>
            <Input data-testid="mobile-quote-contact-name-input" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="John Smith" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div>
            <Label className="text-[var(--m-text-secondary)] text-sm">Position / Title</Label>
            <Input data-testid="mobile-quote-contact-title-input" value={contactForm.job_title} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} placeholder="Site Manager" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-sm">Phone</Label>
              <Input data-testid="mobile-quote-contact-phone-input" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="0400 000 000" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
            </div>
            <div>
              <Label className="text-[var(--m-text-secondary)] text-sm">Email</Label>
              <Input data-testid="mobile-quote-contact-email-input" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="john@example.com" className="bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" />
            </div>
          </div>
        </div>
        <SheetFooter>
          <Button onClick={handleCreateContact} disabled={savingContact} className="w-full h-12 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90" data-testid="mobile-quote-contact-submit-btn">
            {savingContact ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add Contact
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    </>
  );
};

export default MobileQuoteEdit;
